import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useTheme } from '../hooks/useTheme.ts';

interface ConcentrationModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (startConc: number, endConc: number) => void;
  initialStartConc?: number;
  initialEndConc?: number;
}

const ConcentrationModal: React.FC<ConcentrationModalProps> = ({
  show,
  onHide,
  onSave,
  initialStartConc = 0,
  initialEndConc = 0
}) => {
  const { theme } = useTheme();
  const [startConc, setStartConc] = useState<number>(initialStartConc);
  const [endConc, setEndConc] = useState<number>(initialEndConc);

  // Update state when initial values change (when a new step is selected)
  useEffect(() => {
    if (show) {
      setStartConc(initialStartConc);
      setEndConc(initialEndConc);
    }
  }, [show, initialStartConc, initialEndConc]);

  const handleSave = () => {
    onSave(startConc, endConc);
    onHide();
  };

  const handleClose = () => {
    // Reset values when closing without saving
    setStartConc(initialStartConc);
    setEndConc(initialEndConc);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="sm"
      style={{ zIndex: 1060 }}
    >
      <Modal.Header 
        closeButton 
        style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderBottom: `1px solid ${theme === 'dark' ? '#5C646B' : '#e6e6e6'}`
        }}
      >
        <Modal.Title style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
          Sand Ramp Concentrations
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body 
        style={{ 
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          color: theme === 'dark' ? '#e5e5e5' : '#333333'
        }}
      >
        <Form>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Start Target Concentration</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={startConc}
                  onChange={(e) => setStartConc(parseFloat(e.target.value) || 0)}
                  style={{
                    backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                    color: theme === 'dark' ? '#e5e5e5' : '#333333',
                    borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6'
                  }}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>End Target Concentration</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={endConc}
                  onChange={(e) => setEndConc(parseFloat(e.target.value) || 0)}
                  style={{
                    backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                    color: theme === 'dark' ? '#e5e5e5' : '#333333',
                    borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6'
                  }}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      
      <Modal.Footer 
        style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderTop: `1px solid ${theme === 'dark' ? '#5C646B' : '#e6e6e6'}`
        }}
      >
        <Button 
          variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'} 
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConcentrationModal;
